/*
 * Meeting picker for the pre-join screen (v1.0 design section 4.2.3), shown in
 * place of the free-text meeting title when the named_meetings flag is on.
 *
 * Lists the account's named meetings (most recently started first, live ones
 * marked), and lets the moderator choose one, create a new one by name, rename
 * the chosen one, or delete it. A Gagl Solo account sees its single meeting
 * with rename only. The current choice is reported to the parent through
 * onChange as { meetingId, label } or { newName, label }, or null.
 *
 * Developed by Bill Dunning using an AI coding partner through interactive interrogation and direction. No code enters this file until it has been read, understood, and approved by the author; AI output is never committed unreviewed.
 */
import Select from '@atlaskit/select';
import React, { useCallback, useEffect, useState } from 'react';

import { meetingService } from '../../../../../service';
import { InputField } from '../../../base/premeeting';

const NEW_MEETING = '__new_meeting__';
const MAX_NAME_LENGTH = 100;

const ERROR_TEXT = {
    MEETING_NAME_REQUIRED: 'Enter a meeting name.',
    MEETING_NAME_TOO_LONG: `Meeting names can be up to ${MAX_NAME_LENGTH} characters.`,
    MEETING_NAME_TAKEN: 'Another meeting already has that name.',
    MEETING_NOT_FOUND: 'That meeting no longer exists.',
    MEETING_LIVE: 'That meeting is live. End it first.',
    FEATURE_DISABLED: 'Named meetings are not available right now.'
};

/**
 * Turns a Gagl API error response into a sentence for the moderator.
 *
 * @param {Object} error - The axios error.
 * @returns {string}
 */
export function meetingErrorText(error) {
    const data = error && error.response && error.response.data;
    const code = data && data.message;

    if (code === 'MEETING_LIVE' && data.live && data.live.name) {
        return `\u201c${data.live.name}\u201d is live. End it before starting another meeting.`;
    }

    return ERROR_TEXT[code] || (typeof code === 'string' && code) || 'Something went wrong. Please try again.';
}

const linkStyle = {
    cursor: 'pointer',
    marginRight: 16,
    textDecoration: 'underline'
};

/**
 * The meeting picker.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onChange - Receives the current choice.
 * @param {boolean} props.disabled - Disables the controls while a start is in progress.
 * @returns {ReactElement}
 */
export default function MeetingPicker({ onChange, disabled }) {
    const [ meetings, setMeetings ] = useState([]);
    const [ solo, setSolo ] = useState(false);
    const [ selected, setSelected ] = useState(null);
    const [ newName, setNewName ] = useState('');
    const [ renaming, setRenaming ] = useState(false);
    const [ renameValue, setRenameValue ] = useState('');
    const [ busy, setBusy ] = useState(false);
    const [ error, setError ] = useState('');

    const load = useCallback(preferCode => meetingService.listMeetings()
        .then(res => {
            const list = (res && res.data && res.data.meetings) || [];
            const isSolo = Boolean(res && res.data && res.data.solo);
            const keep = preferCode && list.find(m => m.code === preferCode);
            const pick = keep || list.find(m => m.active) || list[0];

            setMeetings(list);
            setSolo(isSolo);
            setSelected(pick ? pick.code : isSolo ? null : NEW_MEETING);
        })
        .catch(e => setError(meetingErrorText(e))), []);

    useEffect(() => {
        load();
    }, [ load ]);

    useEffect(() => {
        if (selected === NEW_MEETING) {
            onChange({ newName,
                label: newName.trim() });

            return;
        }
        const chosen = meetings.find(m => m.code === selected);

        onChange(chosen ? { meetingId: chosen.id,
            label: chosen.name } : null);
    }, [ selected, newName, meetings, onChange ]);

    const current = meetings.find(m => m.code === selected);
    const options = meetings.map(m => {
        return {
            value: m.code,
            label: m.live ? `${m.name} (live)` : m.name
        };
    });

    if (!solo) {
        options.push({
            value: NEW_MEETING,
            label: '+ New meeting'
        });
    }

    const run = (promise, after) => {
        setBusy(true);
        setError('');

        return promise
            .then(after)
            .catch(e => setError(meetingErrorText(e)))
            .finally(() => setBusy(false));
    };

    const startRename = () => {
        setRenameValue(current ? current.name : '');
        setRenaming(true);
        setError('');
    };

    const saveRename = () => {
        if (!current) {
            return;
        }
        run(meetingService.renameMeeting(current.code, renameValue), () => {
            setRenaming(false);

            return load(current.code);
        });
    };

    const deleteCurrent = () => {
        if (!current) {
            return;
        }
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete \u201c${current.name}\u201d? Guest links for this meeting will stop working.`)) {
            return;
        }
        run(meetingService.deleteMeeting(current.code), () => load());
    };

    const locked = disabled || busy;

    return (
        <div className = 'gagl-meeting-picker'>
            <Select
                className = 'multi-select'
                classNamePrefix = 'react-select'
                inputId = 'gagl-meeting-picker'
                isDisabled = { locked }
                isMulti = { false }
                isSearchable = { false }
                onChange = { option => {
                    setSelected(option.value);
                    setRenaming(false);
                    setError('');
                } }
                options = { options }
                placeholder = 'Choose a meeting'
                value = { options.find(o => o.value === selected) || null } />
            {selected === NEW_MEETING && (
                <div style = {{ marginTop: 10 }}>
                    <InputField
                        autoFocus = { true }
                        onChange = { value => setNewName(value.slice(0, MAX_NAME_LENGTH)) }
                        placeHolder = 'New meeting name'
                        value = { newName } />
                </div>
            )}
            {current && !renaming && (
                <div style = {{ marginTop: 8,
                    fontSize: 13 }}>
                    <span
                        onClick = { locked ? undefined : startRename }
                        role = 'button'
                        style = { linkStyle }
                        tabIndex = { 0 }>
                        Rename
                    </span>
                    {!solo && !current.live && (
                        <span
                            onClick = { locked ? undefined : deleteCurrent }
                            role = 'button'
                            style = { linkStyle }
                            tabIndex = { 0 }>
                            Delete
                        </span>
                    )}
                </div>
            )}
            {current && renaming && (
                <div style = {{ marginTop: 10 }}>
                    <InputField
                        autoFocus = { true }
                        onChange = { value => setRenameValue(value.slice(0, MAX_NAME_LENGTH)) }
                        onSubmit = { saveRename }
                        placeHolder = 'Meeting name'
                        value = { renameValue } />
                    <div style = {{ marginTop: 8,
                        fontSize: 13 }}>
                        <span
                            onClick = { locked ? undefined : saveRename }
                            role = 'button'
                            style = { linkStyle }
                            tabIndex = { 0 }>
                            Save
                        </span>
                        <span
                            onClick = { locked ? undefined : () => setRenaming(false) }
                            role = 'button'
                            style = { linkStyle }
                            tabIndex = { 0 }>
                            Cancel
                        </span>
                    </div>
                </div>
            )}
            {error && <div style = {{ color: 'red',
                marginTop: 8 }}>{error}</div>}
        </div>
    );
}
